import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";

const LIMIT = 20;
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/** Asks for a reason, then removes one Power entry. */
const RemoveModal = ({ row, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => setReason(""), [row]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { data, error } = await apiCall("POST", "/power/remove", { powerId: row.powerId, reason: reason.trim() });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not remove Power.");
    toast.success(data?.message || "Power removed.");
    onDone();
  };

  return (
    <Modal show={!!row} onHide={saving ? undefined : onClose} centered className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Remove Power</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {row && (
            <>
              <p className="mb-2">
                Remove <strong>{inr(row.amount)}</strong> Power from <strong>{row.from}</strong>
                {row.to ? <> to <strong>{row.to}</strong></> : null}?
              </p>
              <p className="ps-muted ps-small">
                Only this step is removed; the other steps of the same give keep counting.
                {row.to ? ` It stops counting for ${row.to}'s rank and rewards; a rank reached only because of it goes back down.` : ""}
              </p>
              <Form.Group>
                <Form.Label>Reason</Form.Label>
                <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. given by mistake" required />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="danger" disabled={saving || !reason.trim()}>
            {saving ? <Spinner size="sm" /> : "Remove"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

/**
 * Give Power: business from a bottom ID up the sponsor chain to a top ID, without a package. Each
 * step is its own entry (the member gives it to their sponsor) and counts for that sponsor's rank
 * and rewards only; nothing goes above the top ID. It pays no income and activates no ID.
 */
const GivePower = () => {
  const [form, setForm] = useState({ from: "", to: "", amount: "", note: "" });
  const [giving, setGiving] = useState(false);
  const [steps, setSteps] = useState(null);
  const [stepsError, setStepsError] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [removing, setRemoving] = useState(null);

  const query = useCallback(
    (p, l) => {
      const params = new URLSearchParams({ page: p, limit: l });
      if (applied) params.set("search", applied);
      if (status) params.set("status", status);
      return `/power?${params.toString()}`;
    },
    [applied, status]
  );

  const load = useCallback(async () => {
    setLoading(true);
    const { data: res, error } = await apiCall("GET", query(page, LIMIT));
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load Power.");
    setData(res?.data || null);
  }, [query, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied(search.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "from" || k === "to" ? e.target.value.toUpperCase() : e.target.value }));

  // The chain from the bottom ID up to the top ID, checked as the IDs are typed
  useEffect(() => {
    const from = form.from.trim();
    const to = form.to.trim();
    setSteps(null);
    setStepsError("");
    if (!from || !to) return undefined;
    const t = setTimeout(async () => {
      const { data: res, error } = await apiCall("GET", `/power/preview?${new URLSearchParams({ from, to }).toString()}`);
      if (error) return setStepsError(error.message || "Could not check the chain.");
      setSteps(res?.data?.steps || []);
    }, 400);
    return () => clearTimeout(t);
  }, [form.from, form.to]);

  const give = async (e) => {
    e.preventDefault();
    setGiving(true);
    const { data: res, error } = await apiCall("POST", "/power/give", {
      from: form.from.trim(),
      to: form.to.trim(),
      amount: Number(form.amount),
      note: form.note.trim(),
    });
    setGiving(false);
    if (error) return toast.error(error.message || "Could not give Power.");
    toast.success(res?.message || "Power given.");
    setForm({ from: "", to: "", amount: "", note: "" });
    setPage(1);
    load();
  };

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  return (
    <div className="plan-settings activation-requests-page">
      <div className="ps-header">
        <div>
          <h2>Give Power</h2>
          <p>Business from a bottom ID up to a top ID without a package. Each member in the chain gives it to their sponsor, one entry per step; it counts for that sponsor&apos;s rank and rewards, pays no income and activates no ID.</p>
        </div>
        <ExportButtons
          title="Power"
          disabled={!total}
          filters={[status ? `status: ${status}` : "", applied ? `search: ${applied}` : ""].filter(Boolean).join(", ")}
          columns={[
            { header: "#", value: (r) => r.powerId },
            { header: "Give", value: (r) => r.batch ?? "" },
            { header: "Date", value: (r) => moment(r.date).format("DD MMM YYYY, hh:mm A") },
            { header: "From", value: (r) => r.from },
            { header: "From name", value: (r) => r.from_name },
            { header: "To", value: (r) => r.to },
            { header: "To name", value: (r) => r.to_name },
            { header: "Power", value: (r) => r.amount },
            { header: "Note", value: (r) => r.note },
            { header: "Given by", value: (r) => r.given_by },
            { header: "Status", value: (r) => (r.status === "removed" ? `Removed${r.removed?.reason ? ` (${r.removed.reason})` : ""}` : "Counting") },
          ]}
          getRows={() =>
            collectPages(async (p, l) => {
              const { data: res } = await apiCall("GET", query(p, l));
              return { rows: res?.data?.rows || [], pages: res?.data?.pages || 1 };
            })
          }
        />
      </div>

      <div className="ps-stats ar-stats">
        <div className="ps-stat ar-stat ar-stat--approved">
          <span>Power counting</span>
          <strong>{data ? inr(data.summary?.amount) : "—"}</strong>
          <small>{data ? `${data.summary?.count || 0} entries, ${data.summary?.members || 0} members receiving` : ""}</small>
        </div>
      </div>

      <div className="ps-panel">
        <h4 className="mb-3">Give Power</h4>
        <Form onSubmit={give} className="ar-filters">
          <div>
            <Form.Label>Bottom ID (from)</Form.Label>
            <Form.Control size="sm" value={form.from} onChange={set("from")} placeholder="e.g. AD1234567" required />
          </div>
          <div>
            <Form.Label>Top ID (up to)</Form.Label>
            <Form.Control size="sm" value={form.to} onChange={set("to")} placeholder="e.g. AD7654321" required />
          </div>
          <div>
            <Form.Label>Power per step (₹)</Form.Label>
            <Form.Control size="sm" type="number" min="1" step="0.01" value={form.amount} onChange={set("amount")} placeholder="e.g. 50000000" required />
          </div>
          <div className="ar-f-search">
            <Form.Label>Note</Form.Label>
            <Form.Control size="sm" value={form.note} onChange={set("note")} placeholder="e.g. investment handled outside the system" />
          </div>
          <div className="ar-f-reset">
            <Button size="sm" type="submit" className="ar-primary-btn" disabled={giving || !steps?.length || !(Number(form.amount) > 0)}>
              {giving ? <Spinner size="sm" /> : "Give Power"}
            </Button>
          </div>
        </Form>
        {stepsError && <div className="ps-note ps-note-warn mt-2">{stepsError}</div>}
        {steps?.length > 0 && (
          <div className="ps-note ps-note-info mt-2">
            <div className="mb-1">
              {steps.length} {steps.length === 1 ? "entry" : "entries"}
              {Number(form.amount) > 0 ? `, ${inr(form.amount)} each` : ""}. Nothing goes above {steps[steps.length - 1].to}. No income is paid on it.
            </div>
            <div className="ps-small">
              {steps.map((s, i) => (
                <div key={i}>
                  {s.from} → <strong>{s.to}</strong>
                  {s.to_name ? ` (${s.to_name})` : ""}
                  {s.to_active ? "" : " · inactive ID"}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
        <div className="ar-filters">
          <div className="ar-f-search">
            <Form.Label>Search</Form.Label>
            <Form.Control size="sm" placeholder="Username, give # or note" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div>
            <Form.Label>Status</Form.Label>
            <Form.Select size="sm" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All</option>
              <option value="active">Counting</option>
              <option value="removed">Removed</option>
            </Form.Select>
          </div>
        </div>

        <div className="ps-table-wrap">
          <Table className="ps-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Give #</th>
                <th>From</th>
                <th>To</th>
                <th className="text-end">Power</th>
                <th>Note</th>
                <th>Given by</th>
                <th>Status</th>
                <th className="text-end"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="ps-empty">{applied || status ? "Nothing matches these filters." : "No Power given yet."}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.powerId}>
                    <td className="ps-small">{moment(r.date).format("DD MMM YYYY, hh:mm A")}</td>
                    <td className="ps-small">{r.batch ?? "—"}</td>
                    <td>
                      <div className="ps-strong">{r.from}</div>
                      <div className="ps-muted ps-small">{r.from_name || ""}</div>
                    </td>
                    <td>
                      <div className="ps-strong">{r.to || "all uplines (old entry)"}</div>
                      <div className="ps-muted ps-small">{r.to_name || ""}{r.to && !r.to_active ? " · inactive ID" : ""}</div>
                    </td>
                    <td className="text-end ps-strong">{inr(r.amount)}</td>
                    <td className="ps-small">{r.note || "—"}</td>
                    <td className="ps-small">{r.given_by || "—"}</td>
                    <td className="ps-small">
                      {r.status === "removed" ? (
                        <>
                          <span className="ps-muted">Removed</span>
                          {r.removed?.reason && <div className="ps-muted">{r.removed.reason}</div>}
                        </>
                      ) : (
                        "Counting"
                      )}
                    </td>
                    <td className="text-end">
                      {r.status !== "removed" && (
                        <Button size="sm" variant="outline-danger" onClick={() => setRemoving(r)}>Remove</Button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        <div className="ar-pagination">
          <div className="ar-pagination-info">
            Showing <strong>{total ? (page - 1) * LIMIT + 1 : 0}–{Math.min(page * LIMIT, total)}</strong> of <strong>{total}</strong>
          </div>
          <div className="ar-pages">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span className="ps-muted ps-small">Page {page} of {pages}</span>
            <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>

      <RemoveModal row={removing} onClose={() => setRemoving(null)} onDone={() => { setRemoving(null); load(); }} />
    </div>
  );
};

export default GivePower;
